import { useEffect } from 'react'
import useVirtuosoSnapshot from '@hooks/useVirtuosoSnapshot'
import useScroll from '@hooks/useScroll'
import styled from '@emotion/styled'
import ConversationListItem from '@components/conversations/ConversationListItem'
import PullToRefresh from 'react-simple-pull-to-refresh'
import { EmptyMessages } from '@elements/EmptyState'
import { CircularProgress } from '@mui/material'
import { Virtuoso } from 'react-virtuoso'
import EndDot from '@elements/EndDot'

const ListClearfix = styled.div`
  height: 96px;
  width: 100%;
`

const ConversationList = ({ conversationData, setScrollTarget, currentTab, uniqueTab, }) => {
  // Sets reference to manage top and bottom bar animations
  const { setScrollRef, scrollRef } = useScroll(setScrollTarget, 500)

  // Get conversaion data for rendering list items
  const {
    items: conversations,
    getInitialItems: getInitialConversations,
    getMoreItems: getMoreConversations,
    isLoading: conversationsLoading,
    isLoadingDelayed: conversationsLoadingDelayed,
    setIsLoading: setConversationsLoading,
    noMoreItems,
    listRef,
    firstRenderRef,
  } = conversationData
  
  // Get virtuoso snapshot state data
  const {
    snapshot: conversationSnapshot,
    restoreStateProp,
  } = useVirtuosoSnapshot(
    uniqueTab,
    firstRenderRef,
    listRef,
  )

  // get conversations on initial render
  useEffect(() => {
    const shouldGetConversations = (
      // gets conversations on initial render, when there's no snapshot
      currentTab === uniqueTab &&
      firstRenderRef.current === true &&
      conversationSnapshot &&
      Object.keys(conversationSnapshot)?.length === 0
    ) || (
      // OR get conversations whenever we're on the current tab, 
      //    but have zero conversations to display for whatever reason
      currentTab === uniqueTab &&
      conversations.length === 0
    )
    if (shouldGetConversations) {
      getInitialConversations()
    } else {
      firstRenderRef.current = false
      setConversationsLoading(false)
    }
  }, [currentTab])

  return (
    <PullToRefresh
      onRefresh={getInitialConversations}
      pullingContent={''}
      isPullable={!conversationsLoading}>
      <Virtuoso
        scrollerRef={node => { if (node) setScrollRef(node) }}
        ref={node => { if (node) listRef.current = node  }}
        style={{ height: window.innerHeight }}
        data={conversations}
        overscan={window.screen.height*3}
        endReached={() => !noMoreItems && getMoreConversations()}
        atBottomThreshold={window.innerHeight}
        { ...restoreStateProp }
        itemContent={index => (
          <>
            {index === 0 && <ListClearfix />}
            <ConversationListItem
              key='index'
              // isFirst={index === 0}
              conversation={conversations[index]}
            />
          </>
        )}
        components={{
          EmptyPlaceholder: () => {
            // TODO: When we refactor this into its own component, we'll probably want to add a delay
            //       So it only renders after a certain number of milliseconds
            if (currentTab == 0 && conversationsLoadingDelayed) {
              return (
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress style={{ marginTop: '10rem', color: '#6A23FF' }} thickness={8} />
                </div>
              )
            } else if (!conversationsLoading) {
              return (
                <EmptyMessages />
              )
            }
          },
          Footer: () => {
            return !conversationsLoadingDelayed && conversations?.length > 0
            ? <EndDot />
            : (
              <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress style={{ margin: '1rem', color: '#6A23FF', visibility: (!conversationsLoading || !conversations || conversations?.length === 0) && 'hidden' }} thickness={8} />
              </div>
            )
          }
        }}
      />
    </PullToRefresh>
  )
}

export default ConversationList
